import { useCallback, useMemo } from 'react'

import { useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { Text, VStack } from 'govwell-ui'

import UnsavedChangesProtector from 'src/components/UnsavedChangesProtector/UnsavedChangesProtector'
import {
  useGetOrganizationAIDashboardSettingsQuery,
  useUpdateOrganizationMutation,
} from 'src/fetch/organizations'
import { useAppForm } from 'src/hooks/use-app-form'
import { useMyOrgId, useRefetchAppState } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey } from 'src/utils/queryClient'

type FormValues = {
  aiChatbotNoSubstantiveAnswerMessage: string
  aiCommunityAssistantDisclaimer: string
}

const BehaviorForm = () => {
  const organizationId = useMyOrgId()
  const { mutateAsync: updateOrganization } = useUpdateOrganizationMutation()
  const queryClient = useQueryClient()
  const refetchAppState = useRefetchAppState()
  const { data: organizationAISettings, refetch } = useGetOrganizationAIDashboardSettingsQuery()
  const handleSubmit = useCallback(
    async ({ value }: { value: FormValues }) => {
      await updateOrganization({
        input: {
          aiChatbotNoSubstantiveAnswerMessage:
            value.aiChatbotNoSubstantiveAnswerMessage.trim() || null,
          aiCommunityAssistantDisclaimer: value.aiCommunityAssistantDisclaimer.trim() || null,
          organizationId,
        },
      })
      await refetchAppState?.()
      await queryClient.invalidateQueries({ queryKey: [QueryKey.OrganizationAISettings] })
    },
    [queryClient, refetchAppState, updateOrganization, organizationId]
  )

  const defaultValues = useMemo(
    () => ({
      aiChatbotNoSubstantiveAnswerMessage:
        organizationAISettings?.organization?.aiChatbotNoSubstantiveAnswerMessage ?? '',
      aiCommunityAssistantDisclaimer:
        organizationAISettings?.organization?.aiCommunityAssistantDisclaimer ?? '',
    }),
    [
      organizationAISettings?.organization?.aiChatbotNoSubstantiveAnswerMessage,
      organizationAISettings?.organization?.aiCommunityAssistantDisclaimer,
    ]
  )
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      await handleSubmit({ value })
      await refetch()
      form.reset()
    },
  })
  useStore(form.store, (s) => s) // Listen for changes
  const hasUnsavedChanges = form.state.isDirty
  const handleDiscardChanges = useCallback(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  return (
    <UnsavedChangesProtector
      hasUnsavedChanges={hasUnsavedChanges}
      onDiscardChanges={handleDiscardChanges}
      onSave={form.handleSubmit}
    >
      <VStack gap={4}>
        <VStack gap={2}>
          <Text.H3>Fallback Response Message</Text.H3>
          <Text>Default message for all queries that don&apos;t match the above routing rules</Text>
          <form.AppField name="aiChatbotNoSubstantiveAnswerMessage">
            {(field) => <field.RichTextInput field={field} />}
          </form.AppField>
        </VStack>
        <VStack gap={2}>
          <Text.H3>Disclaimer</Text.H3>
          <Text>
            Disclaimer message for your Community Assistant to relay at the ends of its responses.
          </Text>
          <form.AppField name="aiCommunityAssistantDisclaimer">
            {(field) => <field.RichTextInput field={field} />}
          </form.AppField>
        </VStack>
      </VStack>
    </UnsavedChangesProtector>
  )
}

export default React.memo(BehaviorForm)
